import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  ScanLine, Search, Plus, LogOut, Star, Loader2, Users, X, Bell, CreditCard, Images, Download, CheckSquare, Upload, GitMerge, UserPlus,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { reminderState } from "../lib/reminders";
import { downloadAllVCards, downloadCSV } from "../lib/vcard";
import CardItem from "../components/CardItem";
import ScannerModal from "../components/ScannerModal";
import CardEditor from "../components/CardEditor";
import ShareDialog from "../components/ShareDialog";
import QRDialog from "../components/QRDialog";
import ReminderDialog from "../components/ReminderDialog";
import BulkScanModal from "../components/BulkScanModal";
import ExportDialog from "../components/ExportDialog";
import ImportDialog from "../components/ImportDialog";
import DuplicatesDialog from "../components/DuplicatesDialog";
import { saveManyToPhoneContacts } from "../lib/saveContact";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");

  const [scannerOpen, setScannerOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const [editorState, setEditorState] = useState(null); // {initial, aiExtracted}
  const [shareCard, setShareCard] = useState(null);
  const [qrCard, setQrCard] = useState(null);
  const [reminderCard, setReminderCard] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (activeFilter === "Starred") params.favorites = true;
      const { data } = await api.get("/cards", { params });
      setCards(data);
    } catch (e) {
      toast.error("Could not load contacts");
    } finally {
      setLoading(false);
    }
  }, [search, activeFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const industries = useMemo(() => {
    const set = new Set(cards.map((c) => c.industry).filter(Boolean));
    return Array.from(set).slice(0, 8);
  }, [cards]);

  const visibleCards = useMemo(() => {
    if (activeFilter === "Reminders") {
      return cards
        .filter((c) => reminderState(c))
        .sort((a, b) => new Date(a.reminder_at) - new Date(b.reminder_at));
    }
    if (["All", "Starred"].includes(activeFilter)) return cards;
    return cards.filter((c) => c.industry === activeFilter);
  }, [cards, activeFilter]);

  const reminderCount = useMemo(() => cards.filter((c) => reminderState(c)).length, [cards]);

  const filters = ["All", "Starred", "Reminders", ...industries];

  const handleExtracted = (data) => {
    setScannerOpen(false);
    setEditorState({ initial: data, aiExtracted: true });
  };

  const handleManual = () => {
    setScannerOpen(false);
    setEditorState({ initial: {}, aiExtracted: false });
  };

  const onSaved = () => load();

  const toggleSelect = (card) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(card.id) ? next.delete(card.id) : next.add(card.id);
      return next;
    });

  const exitSelect = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const allVisibleSelected = visibleCards.length > 0 && visibleCards.every((c) => selectedIds.has(c.id));

  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        visibleCards.forEach((c) => next.delete(c.id));
        return next;
      }
      const next = new Set(prev);
      visibleCards.forEach((c) => next.add(c.id));
      return next;
    });

  const exportSelected = async (kind) => {
    if (selectedIds.size === 0) {
      toast.error("Select at least one contact");
      return;
    }
    setExporting(true);
    try {
      const { data } = await api.get("/cards");
      const chosen = data.filter((c) => selectedIds.has(c.id));
      if (kind === "phone") {
        await saveManyToPhoneContacts(chosen);
        toast.success(`Open the downloaded file to add ${chosen.length} contacts to your phone`);
      } else if (kind === "vcf") {
        downloadAllVCards(chosen, "dextyou-group.vcf");
        toast.success(`Exported ${chosen.length} contacts (.vcf)`);
      } else {
        downloadCSV(chosen, "dextyou-group.csv");
        toast.success(`Exported ${chosen.length} contacts to CSV`);
      }
      exitSelect();
    } catch (e) {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const doDelete = async (card) => {
    try {
      await api.delete(`/cards/${card.id}`);
      setCards((prev) => prev.filter((c) => c.id !== card.id));
      toast.success("Contact removed");
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  const toggleFav = async (card) => {
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, favorite: !c.favorite } : c)));
    try {
      await api.patch(`/cards/${card.id}/favorite`);
      if (activeFilter === "Starred") load();
    } catch (e) {
      toast.error("Could not update");
      load();
    }
  };

  const onReminderSaved = (updated) => {
    setCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  const manageBilling = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Billing portal unavailable");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-28">
      {/* Header */}
      <header data-testid="app-header" className="glass sticky top-0 z-40 px-4 sm:px-6 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0A2540] to-[#2EA6FF] flex items-center justify-center shadow-lg shadow-blue-900/30">
              <span className="font-heading text-white font-extrabold text-lg leading-none">D</span>
            </div>
            <div>
              <span className="font-heading text-lg font-extrabold tracking-tight leading-none block">Dextyou</span>
              <span className="hidden sm:inline text-[10px] text-[#2EA6FF] font-mono tracking-wider">LET ME DEXTYOU</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-slate-400">{user?.name || user?.email}</span>
            <button
              data-testid="select-mode-toggle"
              onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-colors ${
                selectMode ? "border-blue-500 text-blue-300 bg-blue-500/10" : "border-slate-800 text-slate-300 hover:text-white hover:border-blue-500"
              }`}
              title="Select contacts to export"
            >
              <CheckSquare className="w-4 h-4" /> <span className="hidden sm:inline text-sm">{selectMode ? "Done" : "Select"}</span>
            </button>
            <button
              data-testid="export-contacts-button"
              onClick={() => setExportOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-blue-500 transition-colors"
              title="Export contacts"
            >
              <Download className="w-4 h-4" /> <span className="hidden sm:inline text-sm">Export</span>
            </button>
            <button
              data-testid="import-contacts-button"
              onClick={() => setImportOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-blue-500 transition-colors"
              title="Import from phone"
            >
              <Upload className="w-4 h-4" /> <span className="hidden sm:inline text-sm">Import</span>
            </button>
            <button
              data-testid="duplicates-button"
              onClick={() => setDupOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-blue-500 transition-colors"
              title="Find duplicates"
            >
              <GitMerge className="w-4 h-4" /> <span className="hidden sm:inline text-sm">Duplicates</span>
            </button>
            {user?.plan && user?.role !== "admin" && (
              <button
                data-testid="manage-billing-button"
                onClick={manageBilling}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-blue-500 transition-colors"
              >
                <CreditCard className="w-4 h-4" /> <span className="hidden sm:inline text-sm">Billing</span>
              </button>
            )}
            <button
              data-testid="logout-button"
              onClick={logout}
              className="p-2 rounded-lg border border-slate-800 text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
              aria-label="Log out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        {/* Hero / count */}
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="overline text-blue-500 mb-1">Your network</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              {cards.length} {cards.length === 1 ? "contact" : "contacts"}
            </h1>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              data-testid="bulk-scan-button"
              onClick={() => setBulkOpen(true)}
              className="flex items-center gap-2 bg-[#111827] border border-slate-800 hover:border-blue-500 text-slate-200 font-semibold rounded-xl px-4 py-3 transition-colors"
            >
              <Images className="w-5 h-5" /> Bulk
            </button>
            <button
              data-testid="scan-card-button"
              onClick={() => setScannerOpen(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl px-5 py-3 transition-colors shadow-lg shadow-blue-900/30"
            >
              <ScanLine className="w-5 h-5" /> Scan card
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <input
            data-testid="search-contacts-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, company or industry…"
            className="w-full bg-[#111827] border border-slate-800 rounded-2xl pl-12 pr-10 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-6">
          {filters.map((f) => {
            const active = activeFilter === f;
            const testid =
              f === "Starred" ? "filter-starred" : f === "Reminders" ? "filter-reminders" : `industry-filter-chip-${f}`;
            return (
              <button
                key={f}
                data-testid={testid}
                onClick={() => setActiveFilter(f)}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm border transition-colors ${
                  active
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-[#111827] border-slate-800 text-slate-400 hover:border-slate-600"
                }`}
              >
                {f === "Starred" && <Star className={`w-3.5 h-3.5 ${active ? "fill-white" : ""}`} />}
                {f === "Reminders" && <Bell className="w-3.5 h-3.5" />}
                {f}
                {f === "Reminders" && reminderCount > 0 && (
                  <span className={`ml-0.5 text-[10px] px-1.5 rounded-full ${active ? "bg-white/25" : "bg-blue-500/20 text-blue-300"}`}>
                    {reminderCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : visibleCards.length === 0 ? (
          <EmptyState onScan={() => setScannerOpen(true)} hasSearch={!!search || activeFilter !== "All"} />
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence mode="popLayout">
              {visibleCards.map((card) => (
                <CardItem
                  key={card.id}
                  card={card}
                  onShare={setShareCard}
                  onShowQR={setQrCard}
                  onRemind={setReminderCard}
                  onEdit={(c) => setEditorState({ initial: c, aiExtracted: false })}
                  onDelete={setConfirmDelete}
                  onToggleFav={toggleFav}
                  selectMode={selectMode}
                  selected={selectedIds.has(card.id)}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </main>

      {/* Mobile FAB */}
      {!selectMode && (
      <button
        data-testid="bulk-scan-fab"
        onClick={() => setBulkOpen(true)}
        className="sm:hidden fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full bg-[#111827] border border-slate-700 text-blue-400 flex items-center justify-center shadow-xl transition-colors"
        aria-label="Bulk scan"
      >
        <Images className="w-6 h-6" />
      </button>
      )}
      {!selectMode && (
      <button
        data-testid="scan-card-fab"
        onClick={() => setScannerOpen(true)}
        className="sm:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center shadow-2xl shadow-blue-900/50 transition-colors"
        aria-label="Scan card"
      >
        <Plus className="w-7 h-7" />
      </button>
      )}

      {/* Selection action bar */}
      <AnimatePresence>
        {selectMode && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-xl">
          <motion.div
            data-testid="selection-bar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
          >
            <div className="glass rounded-2xl border border-slate-700 shadow-2xl px-3 sm:px-4 py-3 flex items-center gap-1.5 sm:gap-3">
              <span data-testid="selection-count" className="text-sm font-semibold text-white whitespace-nowrap">
                {selectedIds.size} selected
              </span>
              <button
                data-testid="select-all-button"
                onClick={toggleSelectAll}
                className="text-xs text-slate-300 hover:text-blue-400 underline-offset-2 hover:underline whitespace-nowrap"
              >
                {allVisibleSelected ? "Clear" : "Select all"}
              </button>
              <div className="flex-1" />
              <button
                data-testid="export-selected-csv-button"
                onClick={() => exportSelected("csv")}
                disabled={exporting || selectedIds.size === 0}
                className="px-3 py-2 rounded-lg border border-slate-700 text-slate-200 hover:border-blue-500 text-xs font-semibold transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                CSV
              </button>
              <button
                data-testid="save-selected-to-phone-button"
                onClick={() => exportSelected("phone")}
                disabled={exporting || selectedIds.size === 0}
                title="Save to phone contacts (with photos)"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-blue-500/60 text-blue-200 hover:bg-blue-500/10 text-xs font-semibold transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                <UserPlus className="w-4 h-4" /> <span className="hidden sm:inline">To phone</span>
              </button>
              <button
                data-testid="export-selected-vcf-button"
                onClick={() => exportSelected("vcf")}
                disabled={exporting || selectedIds.size === 0}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} <span className="hidden sm:inline">Export .vcf</span><span className="sm:hidden">.vcf</span>
              </button>
              <button
                data-testid="selection-cancel-button"
                onClick={exitSelect}
                className="p-2 rounded-lg text-slate-400 hover:text-white transition-colors"
                aria-label="Cancel selection"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onExtracted={handleExtracted}
        onManual={handleManual}
      />
      {editorState && (
        <CardEditor
          open={!!editorState}
          onClose={() => setEditorState(null)}
          initial={editorState.initial}
          aiExtracted={editorState.aiExtracted}
          onSaved={onSaved}
        />
      )}
      <BulkScanModal open={bulkOpen} onClose={() => setBulkOpen(false)} onSaved={onSaved} />
      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
      <DuplicatesDialog open={dupOpen} onClose={() => setDupOpen(false)} onChanged={load} />
      <ShareDialog open={!!shareCard} onClose={() => setShareCard(null)} card={shareCard} />
      <QRDialog open={!!qrCard} onClose={() => setQrCard(null)} card={qrCard} />
      <ReminderDialog open={!!reminderCard} onClose={() => setReminderCard(null)} card={reminderCard} onSaved={onReminderSaved} />

      {/* Delete confirm */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/70" onClick={() => setConfirmDelete(null)} />
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              className="relative bg-[#111827] border border-slate-800 rounded-2xl p-6 max-w-sm w-full"
            >
              <h3 className="font-heading text-lg font-bold mb-1">Delete contact?</h3>
              <p className="text-sm text-slate-400 mb-5">
                Remove <span className="text-white font-medium">{confirmDelete.name || confirmDelete.company}</span> from your Rolodex. This can't be undone.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:text-white text-sm transition-colors">Cancel</button>
                <button data-testid="confirm-delete-button" onClick={() => doDelete(confirmDelete)} className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyState({ onScan, hasSearch }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 text-center"
    >
      <div className="w-20 h-20 rounded-2xl bg-[#111827] border border-slate-800 flex items-center justify-center mb-5 dotted-bg">
        <Users className="w-9 h-9 text-slate-600" />
      </div>
      <h3 className="font-heading text-xl font-bold mb-1">
        {hasSearch ? "No matches" : "Your Rolodex is empty"}
      </h3>
      <p className="text-sm text-slate-400 max-w-xs mb-6">
        {hasSearch
          ? "Try a different name, company or industry."
          : "Scan your first business card and let AI fill in the details."}
      </p>
      {!hasSearch && (
        <button
          onClick={onScan}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl px-6 py-3 transition-colors"
        >
          <ScanLine className="w-5 h-5" /> Scan a card
        </button>
      )}
    </motion.div>
  );
}
